import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Plus, Users } from "lucide-react";
import { listHubs, joinHub } from "@/api";
import { useApp } from "@/App";
import { useHubSession, errMsg } from "@/hooks/useHubSession";
import { HubCard } from "@/components/hubs/HubCard";
import { CreatorLeaderboard } from "@/components/hubs/CreatorLeaderboard";

const SORTS = [{ id: "trending", label: "Trending" }, { id: "top", label: "Top Earners" }, { id: "newest", label: "Newest" }];

export default function HubsPage() {
  const { showToast } = useApp();
  const { address, withSession } = useHubSession();
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("trending");
  const [tag, setTag] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => listHubs({ q, sort, tag, address: address || "" }).then(setData).catch(() => setData({ hubs: [], tags: [] })), [q, sort, tag, address]);
  useEffect(() => { const id = setTimeout(load, 300); return () => clearTimeout(id); }, [load]);

  const join = async (h) => {
    if (!address) return showToast("Connect your wallet to join", "error");
    setBusy(true);
    try { await withSession((t) => joinHub(t, h.slug, "")); showToast(`Joined ${h.name}`); load(); }
    catch (e) { showToast(errMsg(e), "error"); }
    setBusy(false);
  };

  return (
    <div className="pt-24 pb-20" data-testid="hubs-page">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[260px]">
            <div className="chip inline-flex text-orange-300 border-orange-500/40"><Users size={12} /> Social-to-Earn</div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black mt-3">Flame Hubs</h1>
            <p className="text-base md:text-lg text-slate-400 mt-2 max-w-2xl">Build a community, grow it, and earn real FLAME from milestones, engagement and invites.</p>
          </div>
          <Link to="/hubs/new" className="btn-tactile btn-flame flex items-center gap-2" data-testid="hubs-create-button"><Plus size={16} /> Launch a Hub</Link>
        </div>

        <div className="mt-8 flex items-center gap-2 bg-[#181A26] border border-[#2A2E45] rounded-2xl px-4 py-3 max-w-xl">
          <Search size={16} className="text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search hubs" className="bg-transparent outline-none flex-1" data-testid="hubs-search-input" />
        </div>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="hubs-filters">
          {SORTS.map((s) => (
            <button key={s.id} onClick={() => setSort(s.id)} data-testid={`hubs-sort-${s.id}`}
              className={`chip transition-colors ${sort === s.id ? "border-orange-500 text-orange-300 bg-orange-500/10" : ""}`}>{s.label}</button>
          ))}
          <span className="w-px bg-[#2A2E45] mx-1" />
          {(data?.tags || []).map((t) => (
            <button key={t} onClick={() => setTag(tag === t ? "" : t)} data-testid={`hubs-tag-${t.toLowerCase().replace(/\s+/g, "-")}`}
              className={`chip transition-colors ${tag === t ? "border-cyan-400 text-cyan-300 bg-cyan-500/10" : ""}`}>{t}</button>
          ))}
        </div>

        <div className="mt-8 grid lg:grid-cols-[1fr_360px] gap-8 items-start">
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="hubs-grid">
            {data === null && <div className="text-slate-500">Loading hubs…</div>}
            {data?.hubs.length === 0 && (
              <div className="card-tile sm:col-span-2 xl:col-span-3 text-center py-12" data-testid="hubs-empty">
                <p className="text-slate-300 font-display text-xl">No hubs here yet</p>
                <p className="text-sm text-slate-500 mt-1">Be the first creator to light one up.</p>
                <Link to="/hubs/new" className="btn-tactile btn-flame mt-4 inline-block" data-testid="hubs-empty-create">Launch a Hub</Link>
              </div>
            )}
            {data?.hubs.map((h) => <HubCard key={h.id} h={h} onJoin={join} busy={busy} />)}
          </div>
          <CreatorLeaderboard />
        </div>
      </div>
    </div>
  );
}
